import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, GitPullRequestArrow, Play, ShieldCheck, XCircle } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { friendlyError } from '@/lib/errors';
import { confirmDialog, promptDialog } from '@/lib/dialogs';
import { useToastStore } from '@/store/toastStore';

// ============================================================================
// СЭД: панель «Согласование» на форме записи (SSoT — сервер, ApprovalService):
// шаблоны маршрутов, текущий шаг, решения Approve/Reject с комментарием,
// история шагов и статус маршрута. Одна активная запись на документ.
// ============================================================================

interface StepInfo {
  order: number;
  name: string;
  kind: string;
  assigneeType: string;
  assigneeValue: string;
  status: string;
  actedBy: string;
  actedAt: string;
  comment: string;
}
interface InstanceInfo {
  id: string;
  routeCode: string;
  entityType: string;
  entityCode: string;
  entityId: string;
  status: string;
  currentStep: number;
  stepsTotal: number;
  startedAt: string;
  finishedAt: string;
  comment: string;
  steps: StepInfo[];
}
interface TemplateInfo {
  code: string;
  synonym: string;
  steps: { name: string }[];
}

const STATUS_LABEL: Record<string, string> = {
  running: 'В процессе',
  approved: 'Согласовано',
  rejected: 'Отклонено',
  canceled: 'Отменено',
};
const STATUS_CLASS: Record<string, string> = {
  running: 'bg-blue-500/10 text-blue-600 border-blue-500/25',
  approved: 'bg-green-500/10 text-green-600 border-green-500/25',
  rejected: 'bg-red-500/10 text-red-600 border-red-500/25',
  canceled: 'bg-muted text-muted-foreground border-border',
};
const STEP_LABEL: Record<string, string> = {
  pending: 'ожидает',
  approve: 'согласовано',
  reject: 'отклонено',
  skipped: 'пропущено',
};

export function ApprovalPanel({ entityType, entityCode, entityId }: {
  entityType: string;
  entityCode: string;
  entityId: string;
}) {
  const [instances, setInstances] = useState<InstanceInfo[]>([]);
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const [busy, setBusy] = useState(false);
  const [showPick, setShowPick] = useState(false);

  const load = useCallback(async () => {
    if (!entityId || !entityCode) return;
    try {
      const [ri, rt] = await Promise.all([
        getSDK().approval.listRouteInstances({ entityId }),
        getSDK().approval.listRouteTemplates({ entityType, entityCode }),
      ]);
      setInstances((ri?.items || []) as InstanceInfo[]);
      setTemplates((rt?.items || []) as TemplateInfo[]);
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    }
  }, [entityType, entityCode, entityId]);

  useEffect(() => { void load(); }, [load]);

  const active = instances.find((i) => i.status === 'running') || null;
  const last = active || instances[0] || null;

  const start = async (code: string) => {
    setBusy(true);
    try {
      await getSDK().approval.startRoute({ routeCode: code, entityType, entityCode, entityId });
      setShowPick(false);
      await load();
      useToastStore.getState().showSuccess('Маршрут согласования запущен');
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (inst: InstanceInfo, order: number, action: 'approve' | 'reject') => {
    const comment = await promptDialog(
      action === 'approve' ? 'Комментарий к согласованию (необязательно)' : 'Причина отклонения',
    );
    if (comment === null) return;
    setBusy(true);
    try {
      await getSDK().approval.actRouteStep({ instanceId: inst.id, stepOrder: order, action, comment });
      await load();
      useToastStore.getState().showSuccess(action === 'approve' ? 'Шаг согласован' : 'Маршрут отклонён');
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (inst: InstanceInfo) => {
    if (!await confirmDialog('Отменить маршрут согласования?')) return;
    setBusy(true);
    try {
      await getSDK().approval.cancelRoute({ instanceId: inst.id, comment: 'отменено' });
      await load();
      useToastStore.getState().showSuccess('Маршрут отменён');
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!last && templates.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card/60 p-3 space-y-2" data-approval-panel="1">
      <div className="flex items-center gap-2 text-sm font-medium">
        <GitPullRequestArrow className="h-4 w-4" />
        <span>Согласование</span>
        {last && (
          <span className={`rounded border px-1.5 py-px text-[10px] ${STATUS_CLASS[last.status] || ''}`}>
            {STATUS_LABEL[last.status] || last.status}
            {last.status === 'running' ? ` — шаг ${last.currentStep}/${last.stepsTotal}` : ''}
          </span>
        )}
        <span className="ml-auto flex items-center gap-1">
          {!active && templates.length > 0 && (
            <button
              type="button"
              className="flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50"
              disabled={busy}
              onClick={() => setShowPick((v) => !v)}
            >
              <Play className="h-3 w-3" /> Запустить согласование
            </button>
          )}
          {active && (
            <button
              type="button"
              className="rounded border px-2 py-0.5 text-[11px] hover:bg-accent disabled:opacity-50"
              disabled={busy}
              onClick={() => void cancel(active)}
            >
              Отменить
            </button>
          )}
        </span>
      </div>

      {showPick && !active && (
        <div className="space-y-1 rounded border bg-muted/20 p-2">
          {templates.map((t) => (
            <button
              key={t.code}
              type="button"
              className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[12px] hover:bg-accent disabled:opacity-50"
              disabled={busy}
              onClick={() => void start(t.code)}
            >
              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
              <span className="font-medium">{t.synonym || t.code}</span>
              <span className="text-[10px] text-muted-foreground">
                {t.steps.map((s) => s.name).join(' → ')}
              </span>
            </button>
          ))}
        </div>
      )}

      {last && (
        <div className="space-y-1">
          {last.steps.map((s) => {
            const isCurrent = last.status === 'running' && s.order === last.currentStep;
            return (
              <div key={s.order} className={`flex items-center gap-2 rounded px-2 py-1 text-[12px] ${isCurrent ? 'border border-primary/40 bg-primary/5' : ''}`}>
                <span className="w-5 text-muted-foreground">{s.order}.</span>
                <span className="font-medium">{s.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {s.assigneeType === 'role' ? `роль: ${s.assigneeValue}` : 'персонально'} · {s.kind === 'sign' ? 'подпись' : 'согласование'}
                </span>
                <span className={`rounded border px-1.5 py-px text-[10px] ${s.status === 'approve' ? 'border-green-500/25 bg-green-500/10 text-green-600' : s.status === 'reject' ? 'border-red-500/25 bg-red-500/10 text-red-600' : 'border-border bg-muted text-muted-foreground'}`}>
                  {STEP_LABEL[s.status] || s.status}
                </span>
                {s.comment && <span className="truncate text-[10px] text-muted-foreground" title={s.comment}>«{s.comment}»</span>}
                {isCurrent && (
                  <span className="ml-auto flex gap-1">
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded border border-green-500/30 px-2 py-0.5 text-[11px] text-green-600 hover:bg-green-500/10 disabled:opacity-50"
                      disabled={busy}
                      onClick={() => void act(last, s.order, 'approve')}
                    >
                      <CheckCircle2 className="h-3 w-3" /> Согласовать
                    </button>
                    <button
                      type="button"
                      className="flex items-center gap-1 rounded border border-red-500/30 px-2 py-0.5 text-[11px] text-red-600 hover:bg-red-500/10 disabled:opacity-50"
                      disabled={busy}
                      onClick={() => void act(last, s.order, 'reject')}
                    >
                      <XCircle className="h-3 w-3" /> Отклонить
                    </button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
